import React, { createContext, FC, useCallback, useContext, useEffect, useRef, useState } from 'react';
import WindowContext from './WindowContext';
import { ListingSortingType, useFilterListingContextProvider } from './FilterListingContext';
import FunwooAPI, { ListingListEntity, SearchListingDto } from '@/swagger';
import { isNotEmptyArray } from '@/utils/formatChecker';
import { useUpdateEffect } from 'react-use';
import { useRouter } from 'next/router';

const ListingQueryResultContext = createContext(
  {} as {
    listingResult: Array<Array<ListingListEntity | { sid: 'marketing' }>>;
    loading: boolean;
  }
);

interface ListingQueryResultContextProviderProps {
  defaultListing: Array<Array<ListingListEntity | { sid: 'marketing' }>>;
}

export const convertListingSortingType = (type?: ListingSortingType): SearchListingDto['sorting'] => {
  let sorting: SearchListingDto['sorting'];
  if (type) {
    if (type === ListingSortingType.PRICE_ASC) {
      sorting = {
        sortBy: 'price',
        sort: 'ASC',
      };
    } else if (type === ListingSortingType.PRICE_DESC) {
      sorting = {
        sortBy: 'price',
        sort: 'DESC',
      };
    } else if (type === ListingSortingType.CURRENT_DEPLOYED_AT_ASC) {
      sorting = {
        sortBy: 'current_deployed_at',
        sort: 'ASC',
      };
    } else if (type === ListingSortingType.CURRENT_DEPLOYED_AT_DESC) {
      sorting = {
        sortBy: 'current_deployed_at',
        sort: 'DESC',
      };
    }
  }
  return sorting;
};
const ListingQueryResultContextProvider: FC<ListingQueryResultContextProviderProps> = ({
  children,
  defaultListing,
}) => {
  const [listingResult, setListingResult] = useState<
    Array<
      Array<
        | ListingListEntity
        | {
            sid: 'marketing';
          }
      >
    >
  >(defaultListing ?? []);
  const [noMore, setNoMore] = useState(false);
  const [loading, setLoading] = useState(false);

  const { query } = useRouter();
  const { finialAreaFilterResult, finialBuildTypeFilterResult, finalSortingType, finalListingStatusType } =
    useFilterListingContextProvider();
  const { windowRef, setOnScrollCallBacks } = useContext(WindowContext);

  const page = useRef(1);

  useEffect(() => {
    setNoMore(false);
    page.current = 1;
  }, [query]);

  const requestListing = useCallback(async () => {
    return new Promise<Array<ListingListEntity>>(async (resolve) => {
      const { country, cities } = finialAreaFilterResult;
      setLoading(true);
      const {
        data: { payload },
      } = await FunwooAPI.listingApi.searchList({
        paging: {
          page: page.current,
          pageSize: 12,
        },
        country: country === 'ALL' ? undefined : [country],
        cities,
        buildingType: finialBuildTypeFilterResult,
        listingStatus: finalListingStatusType ?? undefined,
        deprioritizeSoldItem:
          country !== 'ALL' || (finialBuildTypeFilterResult && isNotEmptyArray(finialBuildTypeFilterResult)),
        sorting: convertListingSortingType(finalSortingType),
      });
      if (payload.length == 0) {
        setNoMore(true);
      }
      setLoading(false);
      resolve(payload);
    });
  }, [
    finialAreaFilterResult.cities,
    finialAreaFilterResult.country,
    finialBuildTypeFilterResult,
    finalSortingType,
    finalListingStatusType,
  ]);

  useUpdateEffect(() => {
    setListingResult(defaultListing);
  }, [defaultListing]);

  //listing loading more
  const handleWindowScroll = useCallback(async () => {
    const scrollHeight = windowRef.current?.scrollHeight ?? 0;
    const scrollY = (windowRef.current?.offsetHeight ?? 0) + (windowRef.current?.scrollTop ?? 0);
    if (scrollY / scrollHeight > 0.8 || (page.current == 0 && scrollY / scrollHeight > 0.4)) {
      if (loading || noMore) return;
      page.current = page.current + 1;
      const data = await requestListing();
      if (data.length <= 0) return;
      setListingResult((listings) => listings.concat([data]));
    }
  }, [setLoading, page, loading, requestListing, noMore]);

  useEffect(() => {
    const id = (Math.random() * 10000).toString();
    setOnScrollCallBacks((callbacks) => {
      callbacks.set(id, handleWindowScroll);
      return new Map(callbacks);
    });
    return () => {
      setOnScrollCallBacks((callback) => {
        callback.delete(id);
        return new Map(callback);
      });
    };
  }, [handleWindowScroll, setOnScrollCallBacks]);

  return (
    <ListingQueryResultContext.Provider
      value={{
        listingResult,
        loading,
      }}
    >
      {children}
    </ListingQueryResultContext.Provider>
  );
};
const useListingQueryResultContextProvider = () => {
  return useContext(ListingQueryResultContext);
};
export { useListingQueryResultContextProvider, ListingQueryResultContextProvider };
